import { useMutation, useQueryClient } from '@tanstack/react-query';
import aiCrmApi from '../api/aiCrmApi';
import { LeadFormData } from '../components/LeadForm';

export interface CreateLeadData extends LeadFormData {
  // Additional fields that might be needed
}

export interface UpdateLeadData extends Partial<LeadFormData> {
  id: string;
}

/**
 * Hook for creating a new lead
 */
export const useCreateLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateLeadData) => {
      return await aiCrmApi.leads.createLead(data);
    },
    onSuccess: () => {
      // Invalidate leads query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads'] });
    },
  });
};

/**
 * Hook for updating an existing lead
 */
export const useUpdateLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateLeadData) => {
      return await aiCrmApi.leads.updateLead(id, data);
    },
    onSuccess: (_, variables) => {
      // Invalidate both the list and the specific lead query
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads'] });
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads', variables.id] });
    },
  });
};

/**
 * Hook for deleting a lead
 */
export const useDeleteLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leadId: string) => {
      return await aiCrmApi.leads.deleteLead(leadId);
    },
    onSuccess: () => {
      // Invalidate leads query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads'] });
    },
  });
};

/**
 * Hook for updating lead status
 */
export const useUpdateLeadStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leadId, status }: { leadId: string; status: string }) => {
      return await aiCrmApi.leads.updateLeadStatus(leadId, status);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads'] });
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads', variables.leadId] });
    },
  });
};

/**
 * Hook for updating lead priority
 */
export const useUpdateLeadPriority = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leadId, priority }: { leadId: string; priority: string }) => {
      return await aiCrmApi.leads.updateLeadPriority(leadId, priority);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads'] });
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads', variables.leadId] });
    },
  });
};

/**
 * Hook for adding activity to a lead
 */
export const useAddLeadActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leadId, activity }: { leadId: string; activity: any }) => {
      return await aiCrmApi.leads.addLeadActivity(leadId, activity);
    },
    onSuccess: (_, variables) => {
      // Invalidate activities query for this lead
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads', variables.leadId, 'activities'] });
    },
  });
};

/**
 * Hook for marking a lead's email as verified
 */
export const useMarkEmailVerified = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leadId: string) => {
      return await aiCrmApi.leads.markEmailVerified(leadId);
    },
    onSuccess: (_, leadId) => {
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads'] });
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads', leadId] });
    },
  });
};

/**
 * Hook for triggering AI scoring on a lead
 */
export const useScoreLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leadId: string) => {
      return await aiCrmApi.leads.scoreLead(leadId);
    },
    onSuccess: (_, leadId) => {
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads'] });
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads', leadId] });
    },
  });
};

/**
 * Hook for converting lead to customer
 */
export const useConvertLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leadId: string) => {
      return await aiCrmApi.leads.convertLead(leadId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['ai-crm', 'dashboard'] });
    },
  });
};
